import React from 'react';
import cn from 'classnames';
import Carousel from 'react-multi-carousel';
import styles from './index.module.scss';
import { useRecoilState } from 'recoil';
import { SvgIcon } from '@ui-elements';
import { ModelWalkModelsState, ActiveModelState } from '../store';
import { CompareRangeHeaderWithPostcode } from '~/pages/model-landing/components/model-landing-compare-header';
import { ModelWalkVariantContent } from '../model-walk-variant';
import { useIsMobile } from '~/common/hooks/use-is-mobile';
import { AddCompareModelCTA } from '~/pages/model-landing/components/model-landing-campare-variant-tab';

interface ModelWalkBlockContentProps {
  isTabView: boolean;
}

export const ModelWalkBlockContent = React.forwardRef<
  HTMLDivElement,
  ModelWalkBlockContentProps
>(({ isTabView }, ref) => {
  const { isDesktop } = useIsMobile();
  const [models] = useRecoilState(ModelWalkModelsState);
  const [activeModel, setActiveModel] = useRecoilState(ActiveModelState);

  const carouselRef = React.useRef<Carousel>(null);

  const modelTabs = React.useMemo(
    () => models.map((m) => m.modelName ?? 'Unnamed Model'),
    [models]
  );

  const activeVariants = React.useMemo(
    () => activeModel?.modelVariants ?? [],
    [activeModel]
  );

  const centerMode = React.useMemo(
    () => activeVariants.length < 3,
    [activeVariants]
  );

  const displayedVariants = React.useMemo(() => {
    if (isTabView) {
      return activeModel?.modelVariants ?? [];
    }
    return models.flatMap((m) => m.modelVariants ?? []);
  }, [isTabView, models, activeModel]);

  const onTabSelect = (index: number) => {
    const selected = models[index];
    if (selected) {
      setActiveModel(selected);
      carouselRef.current?.goToSlide(0);
    }
  };

  return (
    <div className={cn(styles.modelWalkSection, 'full-device-width')}>
      {activeModel && (
        <>
          <CompareRangeHeaderWithPostcode
            headingTag="h1"
            heading={activeModel.leadInHeading}
            headingTooltip={activeModel.leadInHeadingDisclaimer}
            subHeading={activeModel.leadInText}
            style={{ paddingBottom: '35px', backgroundColor: '#f2f3f4' }}
          />

          {!activeModel.hideCompareRangeButton && (
            <div className={styles.compareCta}>
              <AddCompareModelCTA
                onClick={() => {}}
                modelName={activeModel.modelName ?? ''}
                isMobile={false}
              />
            </div>
          )}

          {isTabView && (
            <ModelTabs
              tabItems={modelTabs}
              activeIndex={models.findIndex(
                (m) => m.modelId === activeModel.modelId
              )}
              onSelect={onTabSelect}
            />
          )}

          <div ref={ref} className={styles.carouselWrapper}>
            <Carousel
              ref={carouselRef}
              arrows
              swipeable
              centerMode={isDesktop() ? centerMode : false}
              customLeftArrow={<CustomLeftArrow />}
              customRightArrow={<CustomRightArrow />}
              containerClass={cn(
                styles.containerClass,
                centerMode && styles.centerMode
              )}
              sliderClass={styles.sliderClass}
              responsive={{
                desktop: {
                  breakpoint: { max: 4000, min: 1024 },
                  items: centerMode ? activeVariants.length ?? 3 : 3,
                  slidesToSlide: 3,
                },
                tablet: {
                  breakpoint: { max: 1024, min: 464 },
                  items: 2,
                  slidesToSlide: 2,
                },
                mobile: {
                  breakpoint: { max: 464, min: 0 },
                  items: 1,
                  slidesToSlide: 1,
                },
              }}
            >
              {displayedVariants.map((variant) => (
                <ModelWalkVariantContent
                  key={variant.pimIdentifier}
                  hideGfv={activeModel.hideGfv}
                  modelName={activeModel.modelName}
                  modelPimIdentifier={activeModel.modelPimIdentifier}
                  {...variant}
                />
              ))}
            </Carousel>
          </div>
        </>
      )}
    </div>
  );
});
interface ModelTabsProps {
  tabItems: string[];
  activeIndex: number;
  onSelect: (index: number) => void;
}
const ModelTabs = React.memo<ModelTabsProps>(
  ({ tabItems, onSelect, activeIndex }) => {
    return tabItems.length > 1 ? (
      <div className={styles.marketingCategoryWrapper}>
        <div className={styles.marketingCategoryTabs}>
          {tabItems.map((item, index) => (
            <div
              key={item}
              onClick={() => onSelect(index)}
              className={cn(
                styles.marketingCategoryItem,
                activeIndex === index && styles.marketingCategoryActive
              )}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
    ) : null;
  }
);
interface ArrowsProps {
  onClick?: () => void;
}
const CustomLeftArrow = ({ onClick }: ArrowsProps) => {
  return (
    <SvgIcon
      className={styles.customLeftArrow}
      type="chevronLeft"
      size={2}
      onClick={onClick}
    />
  );
};

const CustomRightArrow = ({ onClick }: ArrowsProps) => {
  return (
    <SvgIcon
      className={styles.customRightArrow}
      type="chevronRight"
      size={2}
      onClick={onClick}
    />
  );
};
